import Loading from '../Loading';
import { getApiUrl } from '../../config/runtimeConfig';
/**
 * WhotGame.jsx - Whot card game component for 9jaWin
 *
 * Connects to /whot Socket.IO namespace with JWT auth.
 * Renders Whot playing cards, opponent displays, and game controls.
 *
 * Props:
 *   matchId   - Stake game ID
 *   jwtToken  - User's JWT token
 *   onGameEnd - Callback when game finishes
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../../services/api';
import PlatformConfirmModal from '../PlatformConfirmModal';
import WhotEmotePicker from './WhotEmotePicker';
import WhotTextChat from './WhotTextChat';
import GithubWhotBoard from './github-whot-source/GithubWhotBoard';
import WhotVoiceChat from './WhotVoiceChat';
import whotVoiceAnnouncer from '../../services/whotVoiceAnnouncer';
import { shouldAlertForTurn, readVibrationPreference } from './whotInteraction';

// ============================================================
// CONSTANTS
// ============================================================

const SHAPE_CONFIG = {
  circle:   { color: '#DC2626', bg: '#FEF2F2', label: 'Circle',   emoji: '🔴' },
  triangle: { color: '#2563EB', bg: '#EFF6FF', label: 'Triangle', emoji: '🔺' },
  cross:    { color: '#16A34A', bg: '#F0FDF4', label: 'Cross',    emoji: '❌' },
  square:   { color: '#D97706', bg: '#FFFBEB', label: 'Square',   emoji: '🟫' },
  star:     { color: '#7C3AED', bg: '#F5F3FF', label: 'Star',     emoji: '⭐' },
  whot:     { color: '#B45309', bg: '#FFFBEB', label: 'WHOT!',    emoji: '🃏' },
};

const PLAYABLE_SHAPES = ['circle', 'triangle', 'cross', 'square', 'star'];

const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

// ============================================================
// SUB-COMPONENTS
// ============================================================

/** Render a single Whot card */
function CardView({ card, isPlayable, isSelected, onClick, size, faceDown }) {
  const dims = { sm: { w: 52, h: 74, icon: 24, num: 10 }, md: { w: 70, h: 98, icon: 34, num: 13 }, lg: { w: 88, h: 124, icon: 44, num: 16 } };
  const dim = dims[size || 'md'];
  const cfg = SHAPE_CONFIG[card.shape] || SHAPE_CONFIG.circle;

  if (faceDown) {
    return (
      <div style={{
        width: dim.w, height: dim.h, flexShrink: 0,
        background: 'linear-gradient(135deg, #065f46, #064e3b)',
        borderRadius: '8px', border: '2px solid #047857',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: 0.8,
      }}>
        <div style={{ width: '70%', height: '75%', border: '2px solid #059669', borderRadius: '4px' }} />
      </div>
    );
  }

  const hoverClass = onClick ? 'pointer' : 'default';
  const playableRing = isPlayable ? '2px solid #fbbf24' : 'none';
  const selectedOffset = isSelected ? '-8px' : '0px';
  const opacity = !isPlayable && onClick ? 0.6 : 1;

  return (
    <div
      onClick={isPlayable || !onClick ? onClick : undefined}
      style={{
        width: dim.w, height: dim.h, flexShrink: 0,
        background: '#fff', borderRadius: '8px',
        border: `2px solid ${cfg.color}`,
        boxShadow: isPlayable ? `0 4px 12px ${cfg.color}44` : '0 2px 8px rgba(0,0,0,0.3)',
        position: 'relative', userSelect: 'none', cursor: isPlayable ? 'pointer' : hoverClass,
        transform: `translateY(${selectedOffset})`,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        opacity,
      }}
    >
      <span style={{ position: 'absolute', top: 2, left: 4, color: cfg.color, fontSize: dim.num, fontWeight: 900, lineHeight: 1 }}>
        {card.number}
      </span>

      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ShapeIcon shape={card.shape} size={dim.icon} color={cfg.color} />
      </div>

      <span style={{ position: 'absolute', bottom: 2, right: 4, color: cfg.color, fontSize: dim.num, fontWeight: 900, lineHeight: 1, transform: 'rotate(180deg)' }}>
        {card.number}
      </span>
    </div>
  );
}

/** SVG shape icon */
function ShapeIcon({ shape, size, color }) {
  const half = size / 2;
  switch (shape) {
    case 'circle':
      return <svg width={size} height={size}><circle cx={half} cy={half} r={half * 0.78} fill={color} /></svg>;
    case 'triangle': {
      const pts = `${half},${size * 0.1} ${size * 0.9},${size * 0.88} ${size * 0.1},${size * 0.88}`;
      return <svg width={size} height={size}><polygon points={pts} fill={color} /></svg>;
    }
    case 'cross': {
      const t = size * 0.32, w = size * 0.36, l = size * 0.08;
      return <svg width={size} height={size}>
        <rect x={t} y={l} width={w} height={size - l * 2} rx={w * 0.2} fill={color} />
        <rect x={l} y={t} width={size - l * 2} height={w} rx={w * 0.2} fill={color} />
      </svg>;
    }
    case 'square':
      return <svg width={size} height={size}><rect x={size * 0.1} y={size * 0.1} width={size * 0.8} height={size * 0.8} rx={size * 0.1} fill={color} /></svg>;
    case 'star': {
      const pts = [];
      for (let i = 0; i < 10; i++) {
        const angle = (i * Math.PI) / 5 - Math.PI / 2;
        const r = i % 2 === 0 ? half * 0.85 : half * 0.38;
        pts.push(`${half + r * Math.cos(angle)},${half + r * Math.sin(angle)}`);
      }
      return <svg width={size} height={size}><polygon points={pts.join(' ')} fill={color} /></svg>;
    }
    case 'whot':
      return <svg width={size} height={size}>
        <text x={half} y={half * 1.15} textAnchor="middle" fontSize={size * 0.38} fontWeight="900" fontFamily="Arial" fill={color}>WHOT</text>
      </svg>;
    default: return null;
  }
}


function PlayerAvatar({ player, size = 44 }) {
  const avatar = player?.avatar;
  const isUrl = typeof avatar === 'string' && (avatar.startsWith('/') || /^https?:\/\//i.test(avatar));
  if (isUrl) {
    return <img src={avatar} alt="Player avatar" loading="eager" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />;
  }
  return <span style={{ fontSize: Math.max(18, Math.round(size * 0.52)) }}>{avatar || '🎴'}</span>;
}

/** Shape picker modal for WHOT wild card */
function ShapePickerModal({ onSelect, onCancel }) {
  return (
    <div style={styles.modalOverlay}>
      <div style={styles.shapeModal}>
        <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: 900, marginBottom: 4 }}>WHOT Card Played!</h2>
        <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: 16 }}>Choose a shape for the next player</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
          {PLAYABLE_SHAPES.map((shape) => (
            <button
              key={shape}
              onClick={() => onSelect(shape)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                padding: 10, borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.05)',
                cursor: 'pointer', transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            >
              <ShapeIcon shape={shape} size={40} color={SHAPE_CONFIG[shape].color} />
              <span style={{ color: SHAPE_CONFIG[shape].color, fontSize: 10, fontWeight: 700 }}>{SHAPE_CONFIG[shape].label}</span>
            </button>
          ))}
        </div>
        <button
          onClick={onCancel}
          style={{
            width: '100%', padding: '8px', border: 'none', background: 'transparent',
            color: '#9ca3af', fontSize: '13px', cursor: 'pointer',
          }}
        >Cancel</button>
      </div>
    </div>
  );
}


function CardCalculationOverlay({ data, onComplete }) {
  const [playerIndex, setPlayerIndex] = useState(0);
  const [phase, setPhase] = useState('intro');
  const [cardIndex, setCardIndex] = useState(-1);
  const [visibleCard, setVisibleCard] = useState(null);
  const [runningTotal, setRunningTotal] = useState(0);
  const [tieRound, setTieRound] = useState(null);
  const [tiePlayer, setTiePlayer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

    const run = async () => {
      if (!data?.players?.length) {
        onComplete?.();
        return;
      }

      setPhase('intro');
      await wait(700);

      const players = data.players;
      for (let index = 0; index < players.length; index += 1) {
        if (cancelled) return;
        const player = players[index];
        setPlayerIndex(index);
        setCardIndex(-1);
        setVisibleCard(null);
        setRunningTotal(0);

        if (player.isWinner || !player.hand?.length) {
          setPhase('winner');
          await wait(700);
          continue;
        }

        setPhase('player');
        await wait(500);

        for (let cardIdx = 0; cardIdx < player.hand.length; cardIdx += 1) {
          if (cancelled) return;
          const card = player.hand[cardIdx];
          setCardIndex(cardIdx);
          setVisibleCard(card);
          setPhase('flip');
          await wait(300);
          if (cancelled) return;
          setPhase('draw');
          await wait(400);
          if (cancelled) return;
          setRunningTotal((current) => current + Number(card?.number || 0));
          setPhase('value');
          await wait(200);
        }

        setPhase('final');
        await wait(500);
      }

      const rounds = data.tiebreaker?.rounds || [];
      for (const round of rounds) {
        if (cancelled) return;
        setTieRound(round.round);
        setPhase('tiebreaker');
        setVisibleCard(null);
        setTiePlayer(null);
        await wait(400);

        for (const draw of round.draws || []) {
          if (cancelled) return;
          setTiePlayer(draw);
          setVisibleCard(draw.card);
          setRunningTotal(draw.runningTotal);
          setPhase('flip');
          await wait(300);
          if (cancelled) return;
          setPhase('draw');
          await wait(400);
          if (cancelled) return;
          setPhase('value');
          await wait(200);
        }
      }

      if (cancelled) return;
      setPhase('complete');
      await wait(900);
      if (!cancelled) onComplete?.();
    };

    run().catch(() => {
      if (!cancelled) onComplete?.();
    });

    return () => {
      cancelled = true;
    };
  }, [data, onComplete]);

  const currentPlayer = data?.players?.[playerIndex] || null;
  const displayName = tiePlayer?.username || currentPlayer?.username || 'Player';
  const displayPosition = currentPlayer?.position || playerIndex + 1;

  return (
    <div style={styles.modalOverlay}>
      <style>{`
        @keyframes whotCardFlip {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(180deg); }
        }
        @keyframes whotCardDraw {
          from { transform: rotateY(180deg) translateY(48px) scale(.78); opacity: .35; }
          to { transform: rotateY(180deg) translateY(0) scale(1); opacity: 1; }
        }
        @keyframes whotValueFade {
          from { opacity: 0; transform: scale(.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div style={styles.cardsCalculationModal}>
        <div style={styles.cardsCalculationTitle}>
          {phase === 'intro' ? 'Calculating remaining cards…' : phase === 'tiebreaker' || tieRound ? 'Tiebreaker — 5-card reveal' : 'Final card-value calculation'}
        </div>
        <div style={styles.cardsCalculationPlayer}>
          {displayName} {tiePlayer ? `• Round ${tieRound}` : `• #${displayPosition}`}
        </div>

        {phase === 'winner' ? (
          <div style={styles.cardsCalculationWinner}>
            <div style={{ fontSize: 54 }}>🏆</div>
            <strong>{currentPlayer?.username || 'Winner'} finished with an empty hand.</strong>
            <span>Winner — card value 0</span>
          </div>
        ) : visibleCard ? (
          <div style={styles.cardsCalculationStage}>
            <div style={{
              perspective: 900,
              width: 88,
              height: 124,
            }}>
              <div style={{
                width: '100%',
                height: '100%',
                transformStyle: 'preserve-3d',
                transform: ['draw', 'value', 'final'].includes(phase) ? 'rotateY(180deg)' : 'rotateY(0deg)',
                transition: 'transform 300ms ease-in-out',
                animation: phase === 'flip' ? 'whotCardFlip 300ms ease-in-out both' : 'none',
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  ...styles.cardsCalculationBack,
                }}>
                  <span>WHOT</span>
                </div>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  animation: phase === 'draw' ? 'whotCardDraw 400ms ease-out both' : 'none',
                }}>
                  <CardView card={visibleCard} size="lg" />
                </div>
              </div>
            </div>
            <div style={{
              ...styles.cardsCalculationValue,
              animation: phase === 'value' ? 'whotValueFade 200ms ease-out both' : 'none',
            }}>
              {visibleCard.shape === 'whot' ? 'WHOT' : `${SHAPE_CONFIG[visibleCard.shape]?.label || visibleCard.shape}`} {visibleCard.number} = {visibleCard.number}
            </div>
            <div style={styles.cardsCalculationTotal}>Total: {runningTotal}</div>
          </div>
        ) : (
          <div style={styles.cardsCalculationStage}>
            <div style={styles.cardsCalculationCount}>
              {currentPlayer?.hand?.length || 0} card{(currentPlayer?.hand?.length || 0) === 1 ? '' : 's'} remaining
            </div>
            <div style={styles.cardsCalculationTotal}>Total: {runningTotal}</div>
          </div>
        )}

        {phase === 'complete' && (
          <div style={styles.cardsCalculationComplete}>
            Final rankings ready — displaying card values and payout.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function WhotGame({ matchId, matchSource = 'stake', jwtToken, onGameEnd }) {
  const socketRef = useRef(null);
  // Match the shared action-security contract exactly:
  // one monotonically increasing sequence per Socket.IO connection plus the
  // current client timestamp and a unique idempotency key.
  const actionSeq = useRef(0);
  const [connected, setConnected] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [localReconnectDeadline, setLocalReconnectDeadline] = useState(null);
  const [voiceSocket, setVoiceSocket] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [gameFinished, setGameFinished] = useState(false);
  const voiceShutdownRef = useRef(null);
  const gameContainerRef = useRef(null);
  const [voiceStates, setVoiceStates] = useState({});
  const handleVoiceStateChange = useCallback((userId, state) => {
    setVoiceStates((prev) => ({ ...prev, [String(userId)]: { ...(prev[String(userId)] || {}), ...state } }));
  }, []);
  const [playerId, setPlayerId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [missTurnNotice, setMissTurnNotice] = useState(null);
  const missTurnNoticeTimer = useRef(null);
  const previousMissedTurnsRef = useRef(null);

  // Shape picker
  const [showShapePicker, setShowShapePicker] = useState(false);
  const [pendingWhotCardId, setPendingWhotCardId] = useState(null);

  // Emotes
  const [emotes, setEmotes] = useState({}); // { userId: { emoteId, username, timestamp } }
  const emoteGameEndedRef = useRef(false);

  // Winner/result flow: the card calculation animation must finish before the payout modal replaces it.
  const [winnerModal, setWinnerModal] = useState(null);
  const [settlementPending, setSettlementPending] = useState(false);
  const [settlementErrorMessage, setSettlementErrorMessage] = useState('');
  const settlementErrorMessageRef = useRef('');
  const [cardsAnimationData, setCardsAnimationData] = useState(null);
  const [showCardsAnimation, setShowCardsAnimation] = useState(false);
  const showCardsAnimationRef = useRef(false);
  const pendingWinnerModalRef = useRef(null);
  // Keep the latest authoritative settlement outside modal state so a
  // settlementCompleted event cannot be lost while the result animation is
  // still running or before the winner modal has mounted.
  const latestSettlementRef = useRef(null);
  const [showResignConfirm, setShowResignConfirm] = useState(false);
  const [resultFriendActions, setResultFriendActions] = useState({});
  const [resultFriendBusy, setResultFriendBusy] = useState({});
  const resultFriendResolutionRef = useRef(0);

  // Card selection
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [clockNow, setClockNow] = useState(Date.now());
  const [achievementUnlock, setAchievementUnlock] = useState(null);
  const playerIdRef = useRef(null);
  const previousTurnRef = useRef(null);
  const gameStateRef = useRef(null);
  const lastReadyActionSequenceRef = useRef(null);
  const pendingActionRef = useRef(null);
  const lastVibratedTurnRef = useRef(null);
  const suppressTurnVibrationRef = useRef(true);
  const [vibrateOnTurn, setVibrateOnTurn] = useState(true);
  const vibrateOnTurnRef = useRef(true);
  const pendingVibrationPreferenceRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const handFlashTimerRef = useRef(null);
  const [handAlertFlash, setHandAlertFlash] = useState(false);
  const [lastTurnChangeAt, setLastTurnChangeAt] = useState('');
  const [lastVibrateReturnValue, setLastVibrateReturnValue] = useState('not called');
  const vibrationDebugEnabled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  useEffect(() => {
    const unlock = () => {
      whotVoiceAnnouncer.unlock();
      try { const AudioContextClass = window.AudioContext || window.webkitAudioContext; if (AudioContextClass && !audioContextRef.current) audioContextRef.current = new AudioContextClass(); if (audioContextRef.current?.state === 'suspended') audioContextRef.current.resume(); audioUnlockedRef.current = Boolean(audioContextRef.current); } catch (_) { /* Audio fallback is best-effort on restricted WebViews. */ }
    };
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('touchstart', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (playerId == null) return;
    const pending = pendingVibrationPreferenceRef.current;
    const enabled = pending == null ? readVibrationPreference(playerId) : pending;
    vibrateOnTurnRef.current = enabled;
    setVibrateOnTurn(enabled);
    if (pending != null) {
      try { window.localStorage.setItem(`9jawin:whot:vibrate:${String(playerId)}`, enabled ? 'on' : 'off'); } catch (_) { /* Keep session preference if storage is unavailable. */ }
      pendingVibrationPreferenceRef.current = null;
    }
  }, [playerId]);

  const triggerFallbackTurnAlert = useCallback(() => {
    setHandAlertFlash(true);
    if (handFlashTimerRef.current) window.clearTimeout(handFlashTimerRef.current);
    handFlashTimerRef.current = window.setTimeout(() => setHandAlertFlash(false), 650);
    try {
      const context = audioContextRef.current;
      if (!audioUnlockedRef.current || !context || context.state !== 'running') return;
      const oscillator = context.createOscillator(); const gain = context.createGain();
      oscillator.type = 'sine'; oscillator.frequency.setValueAtTime(660, context.currentTime);
      gain.gain.setValueAtTime(0.0001, context.currentTime); gain.gain.exponentialRampToValueAtTime(0.035, context.currentTime + 0.015); gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.16);
      oscillator.connect(gain); gain.connect(context.destination); oscillator.start(); oscillator.stop(context.currentTime + 0.17);
    } catch (_) { /* Visual flash remains as the final fallback. */ }
  }, []);

  const handleVibratePreferenceChange = useCallback((enabled) => {
    vibrateOnTurnRef.current = Boolean(enabled);
    setVibrateOnTurn(Boolean(enabled));
    if (playerId == null) pendingVibrationPreferenceRef.current = Boolean(enabled);
    try {
      if (playerId != null) window.localStorage.setItem(`9jawin:whot:vibrate:${String(playerId)}`, enabled ? 'on' : 'off');
    } catch (_) { /* Storage can be unavailable in restricted WebViews; keep this session's preference. */ }
  }, [playerId]);

  useEffect(() => () => { if (handFlashTimerRef.current) window.clearTimeout(handFlashTimerRef.current); }, []);

  useEffect(() => {
    // The game UI displays whole seconds. Updating the entire game tree 4x/sec
    // was unnecessary and caused avoidable mobile rendering pressure.
    // CSS transitions keep the progress bars visually smooth between ticks.
    const timer = setInterval(() => setClockNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleAuthoritativeTerminal = useCallback((state) => {
    if (!state || state.state !== 'finished') return false;
    emoteGameEndedRef.current = true;
    setEmotes({});
    whotVoiceAnnouncer.setTerminal(matchId, {
      gameSeq: Number(state?.actionSequence || state?.turnStartTime || state?._receivedAt || 0),
      source: 'gameState',
    });
    return true;
  }, [matchId, triggerFallbackTurnAlert]);

  const announceConfirmedState = useCallback((state, viewerId) => {
    whotVoiceAnnouncer.observeGameState(state, viewerId);
  }, []);

  // ==========================================================
  // SOCKET CONNECTION
  // ==========================================================
  useEffect(() => {
    if (!matchId || !jwtToken) return;
    emoteGameEndedRef.current = false;
    latestSettlementRef.current = null;
    settlementErrorMessageRef.current = '';
    setSettlementErrorMessage('');
    setSettlementPending(false);
    setMessage('');
    setGameFinished(false);
    setEmotes({});
    previousTurnRef.current = null;
    previousMissedTurnsRef.current = null;
    whotVoiceAnnouncer.resetForGame(matchId);

    const socket = io(`${SOCKET_URL}/whot`, {
      // Use the same short-lived in-memory access token already resolved by
      // PlayWhot. HttpOnly cookies remain enabled as the server-side fallback.
      auth: { token: jwtToken },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.35,
      timeout: 10000,
    });

    socketRef.current = socket;
    setVoiceSocket(socket);
    actionSeq.current = 0;
    lastReadyActionSequenceRef.current = null;

    const reconnectOnForeground = () => {
      if (document.visibilityState !== 'visible') return;
      if (!socket.connected) socket.connect();
      else socket.emit('joinMatch', { matchId, matchSource });
    };
    document.addEventListener('visibilitychange', reconnectOnForeground);
    window.addEventListener('online', reconnectOnForeground);

    socket.on('connect', () => {
      suppressTurnVibrationRef.current = true;
      actionSeq.current = 0;
      setConnected(true);
      setSocketConnected(true);
      setLocalReconnectDeadline(null);
      setError('');
      setMessage('');
      socket.emit('joinMatch', { matchId, matchSource });
    });

    socket.on('connect_error', (err) => {
      setConnected(false);
      setSocketConnected(false);
      // Socket.IO transport errors are implementation details, not helpful player copy.
      const detail = /auth|token|unauthori[sz]ed/i.test(String(err?.message || ''))
        ? 'Your game session needs to reconnect. Please wait while we restore it.'
        : 'We’re having trouble reaching your match. We’ll keep trying to reconnect.';
      setError(detail);
    });

    socket.io.on('reconnect_attempt', (attempt) => {
      setError('Reconnecting to your match…');
    });

    socket.io.on('reconnect', () => {
      setError('');
    });

    socket.io.on('reconnect_failed', () => {
      setConnected(false);
      setSocketConnected(false);
      setError('Game connection could not be restored. Please retry.');
    });

    socket.on('disconnect', (reason) => {
      pendingActionRef.current = null;
      setConnected(false);
      setSocketConnected(false);
      if (reason !== 'io client disconnect') {
        setError('Your connection was interrupted. We’re trying to restore your seat in the match.');
      }
    });

    socket.on('error', (data) => {
      pendingActionRef.current = null;
      setError(data.message);
      setTimeout(() => setError(''), 5000);
    });

    socket.on('serverError', (data) => {
      pendingActionRef.current = null;
      setError(data?.message || 'The game could not commit that action.');
      setTimeout(() => setError(''), 5000);
    });

    // Settlement remains server-authoritative. This event only exposes a
    // retryable/pending settlement condition to the player; it must not imply
    // a loss, win, refund, or completed settlement.
    socket.on('settlementError', (data) => {
      if (!data || String(data.matchId) !== String(matchId)) return;
      const reviewMessage = data?.message || 'Payout is pending review. Your funds are safe. Support will resolve it.';
      settlementErrorMessageRef.current = reviewMessage;
      setSettlementPending(false);
      setSettlementErrorMessage(reviewMessage);
      pendingWinnerModalRef.current = pendingWinnerModalRef.current
        ? { ...pendingWinnerModalRef.current, settlementPending: false, settlementErrorMessage: reviewMessage, message: reviewMessage }
        : pendingWinnerModalRef.current;
      setWinnerModal(prev => prev ? { ...prev, settlementPending: false, settlementErrorMessage: reviewMessage, message: reviewMessage } : prev);
    });

    socket.on('settlementCompleted', (data) => {
      if (!data || String(data.matchId) !== String(matchId)) return;

      const settlement = {
        payouts: data.payouts || {},
        platformCut: Number(data.platformCut || 0),
        totalPool: Number(data.totalPool || 0),
        settlementPending: false,
      };
      latestSettlementRef.current = settlement;
      setSettlementPending(false);

      // Update both possible terminal-result holders. settlementCompleted can
      // arrive before the card animation finishes and before winnerModal exists.
      if (pendingWinnerModalRef.current) {
        pendingWinnerModalRef.current = {
          ...pendingWinnerModalRef.current,
          ...settlement,
        };
      }
      setWinnerModal((prev) => prev ? {
        ...prev,
        ...settlement,
      } : prev);

      setMessage('Payout completed successfully.');
      window.setTimeout(() => setMessage(''), 3500);
    });

    const showGeneralMarket = (effect, state) => {
      if (effect?.type !== 'GENERAL_MARKET') return;
      const names = (effect.affectedPlayers || [])
        .map((id) => state?.players?.find((p) => String(p.userId) === String(id))?.username)
        .filter(Boolean);
      const playedBy = state?.players?.find((p) => String(p.userId) === String(effect.playedBy))?.username || 'A player';
      setMessage(`General Market! ${playedBy} played it — ${names.join(', ') || 'other players'} drew 1 card.`);
      setTimeout(() => setMessage(''), 4000);
    };

    socket.on('whot:actionEvent', (event) => {
      if (typeof window !== 'undefined' && window.__9JAWIN_WHOT_VOICE_DEBUG__) {
        console.debug('[WhotVoice] WHOT EVENT', { event:'whot:actionEvent', gameId:matchId, payload:event, timestamp:Date.now() });
      }
      const viewerId = playerIdRef.current || playerId;
      const stateForEvent = { ...(gameStateRef.current || {}), matchId, players: gameStateRef.current?.players || [], lastActionEvent: event, actionSequence: event?.actionSequence };
      whotVoiceAnnouncer.observeAuthoritativeEvent(stateForEvent, event, viewerId);
    });

    socket.on('gameStateUpdated', (state) => {
      if (!state || String(state.matchId) !== String(matchId)) return;
      gameStateRef.current = state;
      setGameState({ ...state, _receivedAt: Date.now() });
      const pendingAction = pendingActionRef.current;
      if (pendingAction && Number.isFinite(Number(state?.actionSequence)) && Number(state.actionSequence) > Number(pendingAction.actionSequence)) {
        pendingActionRef.current = null;
        setPendingWhotCardId(null);
        setShowShapePicker(false);
      }
      if (state?.myPlayerId) {
        playerIdRef.current = String(state.myPlayerId);
        setPlayerId(String(state.myPlayerId));
      }
    });

    socket.on('gameState', (state) => {
      gameStateRef.current = state;
      if (['finished','gameOver','game_over','completed','ended'].includes(String(state?.state || state?.status || state?.phase || '').toLowerCase()) || state?.gameOver || state?.isFinished) setGameFinished(true);
      if (typeof window !== 'undefined' && window.__9JAWIN_WHOT_VOICE_DEBUG__) {
        console.debug('[WhotVoice] WHOT EVENT', { event:'gameState', gameId:state?.matchId || matchId, payload:state, timestamp:Date.now() });
      }
      const viewerId = state?.myPlayerId || playerId;
      // Feed the authoritative snapshot into the announcer BEFORE any other
      // React state/update/announcement work. This is the hard synchronization
      // barrier for terminal and newer actionSequence transitions.
      const isTerminalSnapshot = whotVoiceAnnouncer.observeGameState(state);
      if (isTerminalSnapshot || ['finished','gameOver','game_over','completed','ended'].includes(String(state?.state || state?.status || state?.phase || '').toLowerCase()) || state?.gameOver || state?.isFinished) {
        handleAuthoritativeTerminal(state, 'gameState');
        setGameState({ ...state, _receivedAt: Date.now() });
        if (state?.myPlayerId) {
          playerIdRef.current = String(state.myPlayerId);
          setPlayerId(String(state.myPlayerId));
        }
        setSelectedCardId(null);
        setShowShapePicker(false);
        return;
      }
      const nextTurnId = state?.currentPlayerId == null ? null : String(state.currentPlayerId);
      const previousTurnId = previousTurnRef.current;
      const turnKey = `${nextTurnId ?? 'none'}:${state?.turnStartTime ?? state?.actionSequence ?? state?._receivedAt ?? ''}`;
      const didTurnChange = !suppressTurnVibrationRef.current && previousTurnId != null && nextTurnId != null && String(previousTurnId) !== String(nextTurnId);
      if (didTurnChange) setLastTurnChangeAt(new Date().toISOString());
      const shouldAlert = didTurnChange && shouldAlertForTurn({ previousTurnId, nextTurnId, playerId: viewerId, visible: typeof document !== 'undefined' && document.visibilityState === 'visible', enabled: vibrateOnTurnRef.current });
      if (shouldAlert && lastVibratedTurnRef.current !== turnKey) {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
          try { const result = navigator.vibrate(500); setLastVibrateReturnValue(String(result)); if (result === false) triggerFallbackTurnAlert(); }
          catch (_) { setLastVibrateReturnValue('threw'); triggerFallbackTurnAlert(); }
        } else { setLastVibrateReturnValue('unsupported'); triggerFallbackTurnAlert(); }
        lastVibratedTurnRef.current = turnKey;
      }
      suppressTurnVibrationRef.current = false;
      // Turn announcements are generated in exactly one place: announceConfirmedState.
      // Keeping a second trigger here caused the same Your Turn event to interrupt and
      // restart itself on the same authoritative snapshot.
      announceConfirmedState(state, viewerId);
      previousTurnRef.current = nextTurnId;

      if (Array.isArray(state?.players)) {
        const previousMisses = previousMissedTurnsRef.current;
        const nextMisses = new Map(state.players.map((p) => [String(p.userId), Number(p.missedTurns || 0)]));
        if (previousMisses) {
          const missedPlayer = state.players.find((p) => Number(p.missedTurns || 0) > Number(previousMisses.get(String(p.userId)) || 0));
          if (missedPlayer) {
            setMissTurnNotice({ username: missedPlayer.username || 'Player', missedTurns: Number(missedPlayer.missedTurns || 0) });
            if (missTurnNoticeTimer.current) clearTimeout(missTurnNoticeTimer.current);
            missTurnNoticeTimer.current = setTimeout(() => setMissTurnNotice(null), 3500);
          }
        }
        previousMissedTurnsRef.current = nextMisses;
      }

      setGameState({ ...state, _receivedAt: Date.now() });
      const pendingAction = pendingActionRef.current;
      if (pendingAction && Number.isFinite(Number(state?.actionSequence)) && Number(state.actionSequence) > Number(pendingAction.actionSequence)) {
        pendingActionRef.current = null;
      }
      if (state?.myPlayerId) {
        playerIdRef.current = String(state.myPlayerId);
        setPlayerId(String(state.myPlayerId));
      }
      showGeneralMarket(state?.lastSpecialEffect, state);
      setSelectedCardId(null);
      setShowShapePicker(false);

      // Explicit readiness acknowledgement: send once per authoritative
      // action sequence per socket connection. Re-acknowledging every snapshot
      // creates unnecessary client/server traffic and log I/O during active play.
      const readySequence = Number.isFinite(Number(state?.actionSequence))
        ? Number(state.actionSequence)
        : null;
      if (lastReadyActionSequenceRef.current !== readySequence) {
        lastReadyActionSequenceRef.current = readySequence;
        socket.emit('clientReady', { matchId });
      }
    });

    socket.on('whot:specialEffect', (effect) => {
      if (typeof window !== 'undefined' && window.__9JAWIN_WHOT_VOICE_DEBUG__) {
        console.debug('[WhotVoice] WHOT EVENT', { event:'whot:specialEffect', gameId:matchId, payload:effect, timestamp:Date.now() });
      }
      setGameState((prev) => prev ? { ...prev, lastSpecialEffect: effect, _receivedAt: Date.now() } : prev);
      setMessage((prev) => prev);
      // The dedicated event is authoritative for repeated special effects.
      if (effect?.type === 'GENERAL_MARKET') {
        setMessage(`General Market! ${effect.playedBy} played it — ${(effect.affectedPlayers || []).length} other player(s) drew 1 card.`);
        setTimeout(() => setMessage(''), 4000);
      }
    });

    socket.on('shapeSelectionRequired', (data) => {
      setPendingWhotCardId(data.cardId);
      // WHOT is a two-stage transaction: the initial playCard request owns
      // the action lock, but stage 2 (shapeSelected) must be allowed through.
      pendingActionRef.current = null;
      setShowShapePicker(true);
    });

    socket.on('cardsDrawn', (data) => {
      const count = Array.isArray(data?.drawn) ? data.drawn.length : 0;
      setMessage(`Drew ${count} card(s)`);
      setTimeout(() => setMessage(''), 3000);
    });

    socket.on('achievement:unlocked', (achievement) => {
      setAchievementUnlock(achievement || null);
      window.setTimeout(() => setAchievementUnlock(null), 6500);
    });

    socket.on('cardsCalculationAnimation', (data) => {
      if (!data || String(data.matchId) !== String(matchId)) return;
      setCardsAnimationData(data);
      showCardsAnimationRef.current = true;
      setShowCardsAnimation(true);

      // The terminal result may arrive before or after the animation event.
      // Never discard an already-received winner result when the animation
      // mounts; the animation completion handler is the single handoff point.
      if (!pendingWinnerModalRef.current) {
        setWinnerModal(null);
      }
    });

    socket.on('gameEnded', (data) => {
      if (typeof window !== 'undefined' && window.__9JAWIN_WHOT_VOICE_DEBUG__) {
        console.debug('[WhotVoice] WHOT EVENT', { event:'gameEnded', gameId:matchId, payload:data, timestamp:Date.now() });
      }
      emoteGameEndedRef.current = true;
      setGameFinished(true);
      setEmotes({});

      // A completed settlement can win the race with gameEnded. Merge the
      // latest authoritative settlement into the terminal payload before it
      // reaches the modal. Reconnects are also covered because the backend
      // rebuilds gameEnded from the durable whot_settlements snapshot.
      const completedSettlement = latestSettlementRef.current;
      const priorSettlementError = settlementErrorMessageRef.current;
      const terminalData = priorSettlementError
        ? { ...data, settlementPending: false, settlementErrorMessage: priorSettlementError, message: priorSettlementError }
        : (completedSettlement ? { ...data, ...completedSettlement } : data);
      setSettlementErrorMessage(terminalData?.settlementErrorMessage || '');

      // Always retain the authoritative terminal payload until the
      // calculation overlay has completed. This removes the event-order
      // race where gameEnded can arrive before cardsCalculationAnimation.
      setSettlementPending(terminalData?.settlementPending === true);
      pendingWinnerModalRef.current = terminalData;
      if (!showCardsAnimationRef.current) {
        setWinnerModal(terminalData);
      }

      handleAuthoritativeTerminal({ state:'finished', matchId, winner:data?.winner, actionSequence:data?.actionSequence }, 'gameEnded');
    });

    socket.on('emoteReceived', (data) => {
      if (emoteGameEndedRef.current) return;
      if (!data?.emoteId || data?.matchId && String(data.matchId) !== String(matchId)) return;
      setEmotes((prev) => ({ ...prev, [data.userId]: data }));
    });

    socket.on('playerPresence', () => {});
    socket.on('playerDisconnected', (data) => {
      if (data?.reconnectDeadline) setLocalReconnectDeadline(data.reconnectDeadline);
      const graceSeconds = Number(data?.graceSeconds || gameStateRef.current?.reconnectGraceSeconds || 0);
      setMessage(`${data?.username || 'A player'} disconnected. ${graceSeconds > 0 ? `Reconnecting for up to ${graceSeconds} seconds.` : 'Waiting for their connection to return.'}`);
      setTimeout(() => setMessage(''), 5000);
    });
    socket.on('game:resumed', (data) => {
      setError('');
      setMessage(data?.message || 'Your game has been resumed');
      setTimeout(() => setMessage(''), 3000);
    });

    socket.on('playerReconnected', (data) => {
      setLocalReconnectDeadline(null);
      setMessage(`${data.username} reconnected`);
      setTimeout(() => setMessage(''), 3000);
    });

    return () => {
      socket.io.off('reconnect_attempt');
      socket.io.off('reconnect');
      socket.io.off('reconnect_failed');
      document.removeEventListener('visibilitychange', reconnectOnForeground);
      window.removeEventListener('online', reconnectOnForeground);
      socket.disconnect();
      setSocketConnected(false);
      setVoiceSocket(null);
      voiceShutdownRef.current = null;
      if (missTurnNoticeTimer.current) {
        clearTimeout(missTurnNoticeTimer.current);
        missTurnNoticeTimer.current = null;
      }
    };
  }, [matchId, matchSource, jwtToken, announceConfirmedState, handleAuthoritativeTerminal]);


  useEffect(() => {
    return () => { if (matchId) whotVoiceAnnouncer.stop(); };
  }, [matchId]);

  // ==========================================================
  // DERIVED STATE
  // ==========================================================
  // Socket/game snapshots may carry user IDs as numbers while the local
  // authenticated player ID is normalized to a string.  Keep the live turn
  // permission check type-safe without weakening server authorization.
  const isMyTurn = gameState && playerId && String(gameState.currentPlayerId) === String(playerId);
  const myHand = gameState?.myHand || [];
  const playableCardIds = gameState?.playableCardIds || [];
  const isCardPlayable = useCallback((cardId) => (
    playableCardIds.some((id) => String(id) === String(cardId))
  ), [playableCardIds]);
  const topCard = gameState?.topCard;
  const pendingPickUp = gameState?.pendingPickUp || 0;
  const requestedShape = gameState?.requestedShape;
  // Drawing is an explicit action available during any valid turn.
  // Penalty draws remain authoritative on the server; normal draws are not
  // blocked merely because the player currently has a playable card.
  const canDraw = isMyTurn;
  const lastCardDeclared = !!gameState?.lastCardDeclared && String(gameState?.lastCardDeclaredBy) === String(playerId);
  const canDeclareLastCard = isMyTurn && myHand.length === 1 && !lastCardDeclared;
  const lastSkippedPlayerId = gameState?.lastSkippedPlayerId;

  // ==========================================================
  // ACTIONS
  // ==========================================================
  const nextActionMeta = useCallback(() => ({
    actionTimestamp: Date.now(),
    actionSeq: ++actionSeq.current,
    _actionId: `${Date.now()}-${window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`,
  }), []);

  const handleCardClick = useCallback((card) => {
    if (!isMyTurn || pendingActionRef.current || !socketRef.current?.connected) return;
    if (!isCardPlayable(card.id)) return;

    const meta = nextActionMeta();
    const currentSequence = Number(gameStateRef.current?.actionSequence || 0);
    // Lock synchronously before emit so rapid repeated taps cannot double-submit.
    pendingActionRef.current = { actionId: meta._actionId, actionSequence: currentSequence };
    socketRef.current.emit('playCard', { matchId, cardId: card.id, ...meta });
    setSelectedCardId(null);
  }, [isMyTurn, isCardPlayable, matchId, nextActionMeta]);

  const handleShapeSelect = useCallback((shape) => {
    setShowShapePicker(false);
    if (pendingWhotCardId && socketRef.current && !pendingActionRef.current) {
      const meta = nextActionMeta();
      const currentSequence = Number(gameStateRef.current?.actionSequence || 0);
      pendingActionRef.current = { actionId: meta._actionId, actionSequence: currentSequence };
      socketRef.current.emit('shapeSelected', {
        matchId,
        cardId: pendingWhotCardId,
        shape,
        ...meta,
      }, (response) => {
        if (!response?.ok) {
          pendingActionRef.current = null;
          setError(response?.error || 'WHOT shape selection failed. Please try again.');
          setShowShapePicker(true);
          return;
        }
        console.debug('[WHOT] shapeSelected acknowledged', {
          matchId,
          cardId: pendingWhotCardId,
          shape,
          actionSequence: response.actionSequence,
        });
      });
      setPendingWhotCardId(null);
    }
  }, [pendingWhotCardId, matchId, nextActionMeta]);

  const handleDraw = useCallback(() => {
    if (!canDraw || !socketRef.current || pendingActionRef.current) return;
    const meta = nextActionMeta();
    const currentSequence = Number(gameStateRef.current?.actionSequence || 0);
    pendingActionRef.current = { actionId: meta._actionId, actionSequence: currentSequence };
    socketRef.current.emit('drawCard', { matchId, ...meta });
    setSelectedCardId(null);
  }, [canDraw, matchId, nextActionMeta]);

    const handleResign = useCallback(() => {
    if (!socketRef.current) return;
    voiceShutdownRef.current?.(true);
    setShowResignConfirm(true);
  }, []);

  const confirmResign = useCallback(() => {
    setShowResignConfirm(false);
    socketRef.current?.emit('resign', { matchId });
  }, [matchId]);

  const handleCardsAnimationComplete = useCallback(() => {
    showCardsAnimationRef.current = false;
    setShowCardsAnimation(false);
    setCardsAnimationData(null);

    // Publish the terminal result only after the card-value calculation
    // animation completes. If gameEnded arrived first, it is already waiting
    // here; if it arrives later, its handler will display it immediately.
    const pending = pendingWinnerModalRef.current;
    pendingWinnerModalRef.current = null;
    if (pending) setWinnerModal(pending);
  }, []);

  const handleCloseWinner = useCallback(() => {
    setWinnerModal(null);
    if (onGameEnd) onGameEnd(winnerModal);
  }, [onGameEnd, winnerModal]);

  useEffect(() => {
    let cancelled = false;
    const resolutionId = ++resultFriendResolutionRef.current;
    const rankings = winnerModal?.rankings || [];
    setResultFriendActions({});
    setResultFriendBusy({});

    if (!rankings.length || !playerId) {
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const requestsRes = await api.friends.getRequests();
        const requests = requestsRes.data?.requests || [];
        const entries = await Promise.all(rankings.map(async (row) => {
          const id = String(row.userId);
          if (id === String(playerId) || row.isBot || row.userId == null) {
            return [id, { targetUserId: id, status: 'self_or_bot' }];
          }
          try {
            const profileRes = await api.profile.getPublic(row.userId);
            const profile = profileRes.data?.data?.profile || {};
            const status = profile.relationshipStatus || 'not_friends';
            const incoming = status === 'request_received'
              ? requests.find((request) => String(request.fromUserId) === id)
              : null;
            return [id, { targetUserId: id, status, friendshipId: incoming?.friendshipId || null }];
          } catch (_) {
            return [id, { targetUserId: id, status: 'unknown', friendshipId: null }];
          }
        }));
        if (!cancelled && resolutionId === resultFriendResolutionRef.current) {
          setResultFriendActions(Object.fromEntries(entries));
        }
      } catch (_) {
        if (!cancelled && resolutionId === resultFriendResolutionRef.current) {
          setResultFriendActions({});
        }
      }
    })();

    return () => { cancelled = true; };
  }, [winnerModal, playerId]);

  const handleResultFriendAction = useCallback(async (userId) => {
    const id = String(userId);
    const action = resultFriendActions[id];
    if (!action || action.targetUserId !== id || action.status !== 'not_friends' || resultFriendBusy[id]) return;
    setResultFriendBusy((prev) => ({ ...prev, [id]: true }));
    try {
      await api.friends.sendRequest(userId);
      setResultFriendActions((prev) => ({ ...prev, [id]: { ...prev[id], targetUserId: id, status: 'pending' } }));
    } catch (err) {
      const errorText = err.response?.data?.error || 'Unable to send friend request';
      setMessage(errorText);
      window.setTimeout(() => setMessage(''), 3500);
    } finally {
      setResultFriendBusy((prev) => ({ ...prev, [id]: false }));
    }
  }, [resultFriendActions, resultFriendBusy]);

  const handleResultAcceptRequest = useCallback(async (userId, friendshipId) => {
    const id = String(userId);
    const action = resultFriendActions[id];
    if (!friendshipId || !action || action.targetUserId !== id || action.status !== 'request_received' || resultFriendBusy[id]) return;
    setResultFriendBusy((prev) => ({ ...prev, [id]: true }));
    try {
      await api.friends.acceptRequest(friendshipId);
      setResultFriendActions((prev) => ({ ...prev, [id]: { ...prev[id], targetUserId: id, status: 'friends', friendshipId } }));
    } catch (_) {
      setMessage('Unable to accept friend request');
      window.setTimeout(() => setMessage(''), 3500);
    } finally {
      setResultFriendBusy((prev) => ({ ...prev, [id]: false }));
    }
  }, [resultFriendActions, resultFriendBusy]);


  // ==========================================================
  // RENDER HELPERS
  // ==========================================================

  function formatTime(seconds) {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  const timers = gameState?.timers || {};
  const localElapsed = gameState?._receivedAt ? Math.max(0, (clockNow - gameState._receivedAt) / 1000) : 0;
  const matchRemaining = Math.max(0, (timers.matchRemaining || 0) - localElapsed);
  const turnRemaining = Math.max(0, (timers.turnRemaining || 0) - localElapsed);
  const isMyTurnWithTimer = isMyTurn && turnRemaining > 0;
  const reconnectingPlayer = gameState?.players?.find((p) => p.status === 'temporarily_disconnected');
  const reconnectDeadline = reconnectingPlayer?.reconnectDeadline || localReconnectDeadline;
  const reconnectSecondsLeft = reconnectDeadline ? Math.max(0, Math.ceil((reconnectDeadline - clockNow) / 1000)) : 0;
  const reconnectGraceSeconds = Number(gameState?.reconnectGraceSeconds || 0);
  const gameIsPausedForReconnect = gameState?.status === 'waiting_for_player_reconnect';

  // ==========================================================
  // RENDER
  // ==========================================================
  if (!connected || !gameState) {
    return (
      <div style={styles.container}>
        {error ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#fff' }}>
            <div style={{ marginBottom: 12, color: '#f87171' }}>{error}</div>
            {localReconnectDeadline && reconnectSecondsLeft > 0 && (
              <div style={{ marginBottom: 16, color: '#fbbf24', fontWeight: 700 }}>
                Reconnecting to your match: {reconnectSecondsLeft}s remaining
              </div>
            )}
            <button
              type="button"
              onClick={() => socketRef.current?.connect()}
              style={{ padding: '10px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700 }}
            >
              Retry connection
            </button>
          </div>
        ) : (
          <Loading variant="page" label="" />
        )}
      </div>
    );
  }

  return (
    <>
      {showResignConfirm && (
        <PlatformConfirmModal
          title="Resign Match?"
          message="Are you sure you want to resign? You will forfeit your stake."
          cancelLabel="Keep Playing"
          confirmLabel="Resign"
          onCancel={() => setShowResignConfirm(false)}
          onConfirm={confirmResign}
        />
      )}
    <div ref={gameContainerRef} style={styles.container} onPointerDown={() => whotVoiceAnnouncer.unlock()}>
      {canDeclareLastCard && (
        <button
          type="button"
          onClick={() => socketRef.current?.emit('declareLastCard', { matchId })}
          style={{
            display: 'block',
            width: 'min(92%, 360px)',
            margin: '10px auto',
            padding: '12px 18px',
            borderRadius: 10,
            border: '2px solid #f59e0b',
            background: '#fff7ed',
            color: '#9a3412',
            fontWeight: 800,
            fontSize: 16,
            cursor: 'pointer',
          }}
          aria-label="Declare last card"
        >
          LAST CARD
        </button>
      )}

      <GithubWhotBoard
        emotes={emotes}
        gameState={gameState}
        playerId={playerId}
        myHand={myHand}
        isMyTurn={isMyTurn}
        turnRemaining={turnRemaining}
        matchRemaining={matchRemaining}
        missTurnNotice={missTurnNotice}
        statusMessage={error || (reconnectingPlayer ? `${gameIsPausedForReconnect ? 'Game paused' : 'Network interruption'} — ${reconnectingPlayer.username || 'A player'} disconnected. ${reconnectSecondsLeft > 0 ? `${reconnectSecondsLeft}s to reconnect` : 'Reconnection time expired'}` : '') || message || (missTurnNotice ? `${missTurnNotice.username} missed the turn` : '') || gameState?.lastAction || (isMyTurn ? 'Your turn' : '')}
        selectedCardId={selectedCardId}
        onCardClick={handleCardClick}
        onDraw={handleDraw}
        canDraw={canDraw}
        requestedShape={requestedShape}
        pendingPickUp={pendingPickUp}
        formatTime={formatTime}
        voiceStates={voiceStates}
        vibrateOnTurn={vibrateOnTurn}
        onVibrateOnTurnChange={handleVibratePreferenceChange}
        vibrationDebug={vibrationDebugEnabled ? { supported: typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function', enabled: vibrateOnTurn, lastTurnChangeAt, lastVibrateReturnValue } : null}
        handAlertFlash={handAlertFlash && vibrateOnTurn}
      />

      {/* Fixed bottom navigation: communication/UI only; existing handlers/state remain unchanged. */}
      <nav style={styles.bottomNav} aria-label="WHOT game controls" onPointerDown={() => whotVoiceAnnouncer.unlock()}>
        <div style={styles.bottomNavItem}>
          <WhotVoiceChat
            socket={voiceSocket}
            matchId={matchId}
            playerId={playerId}
            players={gameState?.players || []}
            compact
            connected={socketConnected}
            active={!gameFinished}
            authToken={jwtToken}
            gameContainerRef={gameContainerRef}
            onVoiceStateChange={handleVoiceStateChange}
            onVoiceShutdownReady={(shutdown) => { voiceShutdownRef.current = shutdown; }}
          />
        </div>

        <div style={styles.bottomNavItem}>
          <WhotEmotePicker socket={socketRef.current} roomId={matchId} eventPayloadKey="matchId" disabled={!!winnerModal || gameState?.state === 'finished'} />
          <span style={styles.bottomNavLabel}>React</span>
        </div>

        <div style={styles.bottomNavItem}>
          <WhotTextChat socket={socketRef.current} matchId={matchId} playerId={playerId} disabled={!!winnerModal || gameState?.state === 'finished'} visible={true} bottomPlacement />
          <span style={styles.bottomNavLabel}>Chat</span>
        </div>

        <div style={styles.bottomNavItem}>
          <button
            type="button"
            onClick={handleResign}
            aria-label="Exit game"
            title="Exit game"
            style={styles.bottomNavButton}
          >
            <span aria-hidden="true">🚪</span>
          </button>
          <span style={styles.bottomNavLabel}>Exit</span>
        </div>
      </nav>


      {achievementUnlock && (
        <div style={styles.achievementToast} role="status" aria-live="polite">
          <div style={{fontSize:42}}>🏆</div>
          <div style={{flex:1}}>
            <div style={styles.achievementToastTitle}>ACHIEVEMENT UNLOCKED</div>
            <div style={styles.achievementToastName}>{achievementUnlock.name}</div>
            <div style={styles.achievementToastText}>{achievementUnlock.description}</div>
            <button type="button" onClick={() => { setAchievementUnlock(null); window.location.assign('/achievements'); }} style={styles.achievementToastButton}>VIEW ACHIEVEMENT</button>
          </div>
          <button type="button" aria-label="Close achievement notification" onClick={() => setAchievementUnlock(null)} style={styles.achievementToastClose}>×</button>
        </div>
      )}


      {/* Shape Picker Modal */}
      {showShapePicker && (
        <ShapePickerModal onSelect={handleShapeSelect} onCancel={() => setShowShapePicker(false)} />
      )}

      {/* Card-value reveal / tiebreaker animation */}
      {showCardsAnimation && cardsAnimationData && (
        <CardCalculationOverlay data={cardsAnimationData} onComplete={handleCardsAnimationComplete} />
      )}

      {/* Winner Modal */}
      {winnerModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.winModal}>
            {(() => {
              const remainingWinners = (winnerModal.rankings || []).filter((r) => !r.isEliminated);
              const currentPlayerIsWinner = remainingWinners.length === 2
                ? remainingWinners.some((r) => String(r.userId) === String(playerId))
                : String(winnerModal.winner?.userId) === String(playerId);
              return (
                <>
                  <div style={{ fontSize: 48, marginBottom: 8 }}>
                    {currentPlayerIsWinner ? '🎉' : '🎮'}
                  </div>
                  <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 900, marginBottom: 4 }}>
                    {currentPlayerIsWinner ? 'You Win!' : 'Game Over!'}
                  </h2>
                </>
              );
            })()}
            <p style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>{winnerModal.message}</p>
            {settlementPending && (
              <div role="status" aria-live="polite" style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
                ⏳ Payout processing — your game result is final. We are safely completing the wallet settlement.
              </div>
            )}
            {settlementErrorMessage && (
              <div role="alert" aria-live="assertive" style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
                {settlementErrorMessage}
              </div>
            )}
            {winnerModal.walletBalances?.[String(playerId)] && (
              <div role="status" style={{ color: '#72e0bd', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
                New wallet balance: ₦{Number(winnerModal.walletBalances[String(playerId)].balance || 0).toFixed(2)}
              </div>
            )}

            <div style={styles.rankingList}>
              {winnerModal.rankings?.map((r, i) => (
                <div key={r.userId} style={styles.rankingRow}>
                  <span style={{ fontSize: 18 }}>{['🥇', '🥈', '🥉', '4️⃣'][i] || `#${i + 1}`}</span>
                  <span style={{ flex: 1, color: '#fff', fontSize: 13, fontWeight: 600 }}>
                    {r.username}
                  </span>
                  <span style={{ color: '#fbbf24', fontWeight: 700 }}>{Number(r.cardValue || 0)} value</span>
                  <span style={{ color: settlementErrorMessage ? '#fbbf24' : '#22c55e', fontWeight: 700, marginLeft: 8, flexShrink: 0 }}>
                    {settlementErrorMessage
                      ? 'Pending review'
                      : settlementPending
                        ? 'Payout processing…'
                        : (winnerModal.payouts?.[r.userId] !== undefined
                          ? `+₦${Number(winnerModal.payouts[r.userId]).toFixed(2)}`
                          : (!r.isBot && Number(winnerModal.entryFee || 0) > 0 ? `−₦${Number(winnerModal.entryFee).toFixed(2)}` : '₦0'))}
                  </span>
                  {(() => {
                    const id = String(r.userId);
                    const action = resultFriendActions[id];
                    if (!action || action.targetUserId !== id || action.status === 'self_or_bot' || action.status === 'unknown') return null;
                    if (action.status === 'friends') return <span style={styles.resultFriendStatus}>✓ Friends</span>;
                    if (action.status === 'pending') return <span style={styles.resultFriendStatus}>✓ Requested</span>;
                    if (action.status === 'request_received') return (
                      <button
                        type="button"
                        disabled={!!resultFriendBusy[id]}
                        onClick={(e) => { e.stopPropagation(); handleResultAcceptRequest(r.userId, action.friendshipId); }}
                        style={styles.resultFriendAccept}
                      >Accept</button>
                    );
                    return (
                      <button
                        type="button"
                        aria-label={`Add ${r.username || 'player'} as a friend`}
                        title="Add Friend"
                        disabled={!!resultFriendBusy[id]}
                        onClick={(e) => { e.stopPropagation(); handleResultFriendAction(r.userId); }}
                        style={styles.resultFriendAdd}
                      >{resultFriendBusy[id] ? '…' : '+'}</button>
                    );
                  })()}
                </div>
              ))}
            </div>


            <button onClick={handleCloseWinner} style={styles.closeBtn}>
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
    </>

  );
}

// ============================================================
// HELPER STYLES
// ============================================================

function timerStyle(urgent) {
  return {
    background: urgent ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)',
    color: urgent ? '#ef4444' : '#9ca3af',
    padding: '4px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700,
  };
}

function turnTimerStyle(urgent) {
  return {
    background: urgent ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.2)',
    color: urgent ? '#ef4444' : '#22c55e',
    padding: '4px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700,
  };
}

// ============================================================
// STYLES
// ============================================================

const styles = {
  container: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    width: '100%', maxWidth: '100%', minHeight: '100dvh', minWidth: 0,
    background: 'linear-gradient(180deg, #0a0a1a 0%, #16213e 100%)',
    color: '#fff', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '4px', paddingBottom: 'calc(92px + env(safe-area-inset-bottom))', boxSizing: 'border-box', overflowX: 'hidden',
  },
  loadingBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh',
  },
  spinner: {
    width: 48, height: 48, border: '4px solid rgba(255,255,255,0.2)',
    borderTopColor: '#00d4aa', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },
  topBar: {
    width: '100%', maxWidth: 560, display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8,
  },
  stakeBadge: {
    background: 'linear-gradient(135deg, #DC2626, #D97706)', color: '#000',
    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
  },
  playerChip: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '4px 6px', borderRadius: 10, background: 'rgba(255,255,255,0.05)',
    minWidth: 50,
  },
  errorBanner: {
    width: '100%', maxWidth: 560, background: 'rgba(239,68,68,0.15)',
    color: '#ef4444', padding: '8px 16px', borderRadius: 8, marginBottom: 8, fontSize: 13,
  },
  messageBanner: {
    width: '100%', maxWidth: 560, background: 'rgba(255,255,255,0.08)',
    color: '#9ca3af', padding: '8px 16px', borderRadius: 8, marginBottom: 8, fontSize: 13,
  },
  reconnectBanner: {
    width: '100%', maxWidth: 560, boxSizing: 'border-box',
    background: 'rgba(245,158,11,0.12)', color: '#fcd34d',
    border: '1px solid rgba(245,158,11,0.45)', padding: '10px 14px',
    borderRadius: 10, marginBottom: 8, fontSize: 13,
  },
  reconnectBannerText: { lineHeight: 1.5 },
  reconnectProgressTrack: {
    height: 5, overflow: 'hidden', borderRadius: 999, marginTop: 8,
    background: 'rgba(255,255,255,0.14)',
  },
  reconnectProgressFill: {
    height: '100%', borderRadius: 999, background: '#f59e0b',
    transition: 'width 1s linear',
  },
  achievementToast: {
    position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
    width: 'min(92vw, 470px)', boxSizing: 'border-box', display: 'flex', gap: 12, alignItems: 'flex-start',
    padding: 16, borderRadius: 16, background: 'linear-gradient(135deg,#171717,#2a1d05)',
    border: '1px solid #f59e0b', boxShadow: '0 18px 50px rgba(0,0,0,.45)',
  },
  achievementToastTitle: { color:'#f59e0b', fontSize:10, fontWeight:900, letterSpacing:1.2 },
  achievementToastName: { color:'#fff', fontSize:18, fontWeight:900, marginTop:3 },
  achievementToastText: { color:'#bbb', fontSize:12, marginTop:3, lineHeight:1.35 },
  achievementToastButton: { marginTop:9, border:0, borderRadius:8, padding:'7px 10px', background:'#f59e0b', color:'#111', fontSize:11, fontWeight:900, cursor:'pointer' },
  achievementToastClose: { border:0, background:'transparent', color:'#aaa', fontSize:22, cursor:'pointer', lineHeight:1 },
  missTurnNotice: {
    width: 'min(92vw, 420px)', boxSizing: 'border-box', margin: '0 auto 8px',
    padding: '10px 16px', borderRadius: 12, textAlign: 'center',
    background: 'rgba(251,191,36,0.14)', border: '1px solid rgba(251,191,36,0.55)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.22)', zIndex: 20,
  },
  missTurnTitle: { color: '#fbbf24', fontSize: 13, fontWeight: 900, letterSpacing: '.5px' },
  missTurnText: { color: '#fff', fontSize: 14, fontWeight: 700, marginTop: 2 },
  opponentRow: {
    width: '100%', maxWidth: 560, display: 'flex', justifyContent: 'center', gap: 16,
    marginBottom: 12, flexWrap: 'wrap',
  },
  opponentCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: 8, borderRadius: 12, background: 'rgba(255,255,255,0.03)',
    minWidth: 100,
  },
  centerArea: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    marginBottom: 12, width: '100%', maxWidth: 320,
  },
  tableLayout4: {
    width: '100%', maxWidth: 720, minHeight: 'min(680px, 78vh)', display: 'grid',
    gridTemplateColumns: 'minmax(54px, 120px) minmax(0, 1fr) minmax(54px, 120px)',
    gridTemplateRows: 'minmax(90px, auto) minmax(250px, 1fr) minmax(90px, auto)',
    gridTemplateAreas: '"top top top" "left center right" "bottom bottom bottom"',
    alignItems: 'center', justifyItems: 'center', gap: 6, margin: '4px auto 12px',
  },
  tableLayout2: {
    width: '100%', maxWidth: 560, minHeight: 'min(600px, 76vh)', display: 'grid',
    gridTemplateColumns: '1fr', gridTemplateRows: 'minmax(90px, auto) minmax(270px, 1fr) minmax(120px, auto)',
    gridTemplateAreas: '"top" "center" "bottom"', alignItems: 'center', justifyItems: 'center', gap: 8, margin: '4px auto 12px',
  },
  seatTop: { gridArea: 'top', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  seatLeft: { gridArea: 'left', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  seatRight: { gridArea: 'right', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  seatBottom: { gridArea: 'bottom', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  centerCell: { gridArea: 'center', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 },
  emptySeat: { minHeight: 1 },
  seat: { position: 'relative', width: '100%', maxWidth: 112, minWidth: 0, padding: '7px 5px', borderRadius: 14, background: 'rgba(255,255,255,0.055)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, boxSizing: 'border-box', transition: 'all .2s ease' },
  activeSeat: { border: '2px solid #fbbf24', background: 'rgba(251,191,36,0.10)', boxShadow: '0 0 18px rgba(251,191,36,0.22)' },
  disqualifiedSeat: { opacity: 0.55, filter: 'grayscale(.35)' },
  seatAvatar: { width: 46, height: 46, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.08)', border: '2px solid rgba(255,255,255,.12)', overflow: 'hidden' },
  seatName: { maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#fff', fontSize: 11, fontWeight: 800, textAlign: 'center' },
  seatStats: { color: '#fbbf24', fontSize: 10, fontWeight: 800 }, seatMissed: { color: '#9ca3af', fontSize: 9 },
  turnBadge: { marginTop: 2, background: '#fbbf24', color: '#111827', padding: '2px 7px', borderRadius: 8, fontSize: 8, fontWeight: 900 },
  offlineBadge: { color: '#f97316', fontSize: 8, fontWeight: 800 }, outBadge: { color: '#ef4444', fontSize: 8, fontWeight: 900, textAlign: 'center' },
  miniHand: { display: 'flex', justifyContent: 'center', marginTop: 3, height: 12, overflow: 'hidden' },
  miniCard: { width: 7, height: 11, marginLeft: -2, borderRadius: 2, background: 'linear-gradient(180deg,#065f46,#064e3b)', border: '1px solid #047857' },
  // Keep the full-size playable hand out of the narrow seat panel's clipping
  // box. The seat still anchors the player identity, while the hand can use
  // the available mobile width and scroll only when the hand itself is wider.
  mySeatHand: { width: 'max-content', maxWidth: '92vw', minWidth: 0, display: 'flex', justifyContent: 'center', gap: 2, overflowX: 'auto', overflowY: 'visible', padding: '5px 2px 2px', scrollbarWidth: 'none', position: 'relative', zIndex: 6 },
  table: { position: 'relative', width: '100%', maxWidth: 390, minWidth: 0, minHeight: 280, padding: '18px 14px', boxSizing: 'border-box', borderRadius: '28px', background: 'radial-gradient(ellipse at center, #14532d 0%, #0f3d28 58%, #09271c 100%)', border: '8px solid #5b341c', boxShadow: 'inset 0 0 0 2px rgba(255,255,255,.08), 0 12px 35px rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7, overflow: 'visible' },
  tableGlow: { position: 'absolute', inset: 12, borderRadius: 20, border: '1px solid rgba(255,255,255,.08)', pointerEvents: 'none' },
  tableTitle: { position: 'relative', color: '#fbbf24', fontSize: 22, fontWeight: 1000, letterSpacing: 4, textShadow: '0 2px 8px rgba(0,0,0,.5)' },
  tableSubtitle: { position: 'relative', color: '#d1fae5', fontSize: 9, fontWeight: 800, letterSpacing: .7, minHeight: 12 },
  tableCards: { position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 18, width: '100%' },
  pile: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }, pileLabel: { color: '#d1d5db', fontSize: 8, fontWeight: 800, letterSpacing: .4 },
  drawTag: { position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)', background: '#f59e0b', color: '#111', fontSize: 8, fontWeight: 900, padding: '2px 7px', borderRadius: 7 },
  emptyCard: { width: 88, height: 124, borderRadius: 8, border: '2px dashed #4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 10 },
  requestedShapeTag: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '6px 14px',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  pendingPickupTag: {
    background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)',
    borderRadius: 10, padding: '4px 12px', color: '#fca5a5', fontSize: 12, fontWeight: 700,
  },
  lastAction: {
    width: '100%', textAlign: 'center', background: 'rgba(0,0,0,0.3)',
    borderRadius: 8, padding: '6px 12px', color: '#9ca3af', fontSize: 11,
    maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  handArea: {
    width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    marginBottom: 12,
  },
  handRow: {
    display: 'flex', gap: 4, overflowX: 'auto', maxWidth: '100%',
    paddingBottom: 4, justifyContent: 'center', flexWrap: 'wrap',
  },
  drawBtn: {
    padding: '10px 24px', background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    color: '#000', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
    cursor: 'pointer', boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
  },
  bottomNav: {
    position: 'fixed', left: 0, right: 0, bottom: 0, width: '100%',
    minHeight: 68, boxSizing: 'border-box', padding: '6px 8px calc(6px + env(safe-area-inset-bottom))',
    display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'stretch',
    gap: 4, background: 'linear-gradient(180deg, #063c2a 0%, #052e22 100%)',
    borderTop: '1px solid rgba(167,243,208,.18)', zIndex: 200,
    boxShadow: '0 -8px 24px rgba(0,0,0,.28)',
  },
  bottomNavItem: {
    flex: 1, minWidth: 0, minHeight: 56, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 2, color: '#d1fae5',
    position: 'relative',
  },
  bottomNavButton: {
    width: 42, height: 42, minHeight: 42, padding: 0, borderRadius: 12,
    border: '1px solid rgba(255,255,255,.12)', background: 'rgba(255,255,255,.05)',
    color: '#ecfdf5', fontSize: 20, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  bottomNavLabel: {
    color: '#d1fae5', fontSize: 10, lineHeight: 1.1, fontWeight: 800,
    whiteSpace: 'nowrap', textAlign: 'center',
  },
  utilityDropdown: {
    display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6,
    width: 'max-content', maxWidth: 'calc(100vw - 24px)', padding: 4,
    borderRadius: 12, background: 'rgba(3,15,10,.72)',
    border: '1px solid rgba(255,255,255,.12)', backdropFilter: 'blur(8px)',
  },
  soundControl: { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 5 },
  announcerHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, minHeight: 44 },
  announcerLabel: { color: '#fff', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' },
  announcerChevron: { width: 30, height: 30, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 18, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  announcerSettings: { display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0 2px 2px' },
  volume: {
    width: 74, maxWidth: '20vw', accentColor: '#f59e0b',
  },
  actionBtn: {
    width: 44, height: 44, borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 20,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  cardsCalculationModal: {
    width: 'min(92vw, 520px)',
    minHeight: 420,
    background: 'rgba(8, 12, 28, 0.98)',
    border: '1px solid rgba(251,191,36,0.35)',
    borderRadius: 20,
    padding: 24,
    boxShadow: '0 24px 80px rgba(0,0,0,0.55)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  cardsCalculationTitle: { fontSize: 24, fontWeight: 900, color: '#fff', marginBottom: 8 },
  cardsCalculationPlayer: { fontSize: 14, fontWeight: 800, color: '#fbbf24', marginBottom: 18 },
  cardsCalculationStage: { minHeight: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 },
  cardsCalculationBack: { background: 'linear-gradient(135deg,#065f46,#064e3b)', border: '2px solid #059669', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a7f3d0', fontWeight: 900, fontSize: 18 },
  cardsCalculationValue: { minHeight: 24, fontSize: 20, fontWeight: 900, color: '#fbbf24' },
  cardsCalculationTotal: { fontSize: 18, fontWeight: 900, color: '#fff' },
  cardsCalculationCount: { fontSize: 16, color: '#cbd5e1' },
  cardsCalculationWinner: { minHeight: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#fff' },
  cardsCalculationComplete: { marginTop: 14, color: '#22c55e', fontWeight: 800 },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(4px)',
  },
  shapeModal: {
    background: '#16213e', borderRadius: 20, padding: 24,
    border: '1px solid rgba(255,255,255,0.1)', maxWidth: 360, width: '90%',
    textAlign: 'center', boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
  },
  winModal: {
    background: '#16213e', borderRadius: 20, padding: 32,
    border: '1px solid rgba(255,255,255,0.1)', maxWidth: 400, width: '90%',
    textAlign: 'center', boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
  },
  rankingList: {
    display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16,
  },
  rankingRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)', flexWrap: 'wrap',
  },
  resultFriendAdd: {
    width: 32, height: 32, flexShrink: 0, borderRadius: 9,
    border: '1px solid rgba(245,158,11,0.55)', background: 'rgba(245,158,11,0.12)',
    color: '#fbbf24', fontSize: 22, lineHeight: 1, fontWeight: 900,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  resultFriendStatus: {
    minHeight: 28, flexShrink: 0, display: 'inline-flex', alignItems: 'center',
    padding: '5px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)', color: '#aaa', fontSize: 9, fontWeight: 800,
  },
  resultFriendAccept: {
    minHeight: 30, flexShrink: 0, borderRadius: 8, border: 'none',
    background: '#f59e0b', color: '#111', padding: '5px 9px', fontSize: 9,
    fontWeight: 900, cursor: 'pointer',
  },
  closeBtn: {
    background: 'linear-gradient(135deg, #00d4aa, #00997a)', color: '#000',
    border: 'none', padding: '12px 32px', borderRadius: 12, fontSize: 15,
    fontWeight: 700, cursor: 'pointer',
  },
};
